import { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { depositToWallet, getWalletBalance, getWalletTransactions, type WalletTransaction } from '@/hooks/useWallet';



// ── Vibration ────────────────────────────────────────────────────────────────
const vibrationController = {
  isVibrating: false,
  vibrateInterval: null as number | null,
  start: () => {
    if (typeof navigator === 'undefined' || !navigator.vibrate) return;
    if (vibrationController.isVibrating) return;
    vibrationController.isVibrating = true;
    const pulse = () => { if (!vibrationController.isVibrating) return; navigator.vibrate(8); };
    pulse();
    vibrationController.vibrateInterval = window.setInterval(pulse, 45);
  },
  stop: () => {
    if (typeof navigator === 'undefined' || !navigator.vibrate) return;
    vibrationController.isVibrating = false;
    if (vibrationController.vibrateInterval) { clearInterval(vibrationController.vibrateInterval); vibrationController.vibrateInterval = null; }
    navigator.vibrate(0);
  },
  win: () => {
    if (typeof navigator === 'undefined' || !navigator.vibrate) return;
    navigator.vibrate([30, 50, 30, 50, 100]);
  },
};

interface ScratchCardPopupProps {
  isOpen: boolean;
  paidAmount: number;
  scratchPercent: number;
  onClose: (earned: number, newTotal: number, txns: WalletTransaction[]) => void;
}

export default function ScratchCardPopup({ isOpen, paidAmount, scratchPercent, onClose }: ScratchCardPopupProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [isScratching, setIsScratching] = useState(false);
  const [scratchedPercent, setScratchedPercent] = useState(0);
  const [revealed, setRevealed] = useState(false);
  const bonusAmount = useMemo(
    () => Math.max(1, Math.floor(paidAmount * (scratchPercent / 100))),
    [paidAmount, scratchPercent]
  );
  const [animStep, setAnimStep] = useState(0);
  const [walletAdded, setWalletAdded] = useState(false);
  const isDrawingRef = useRef(false);
  const lastPosRef = useRef<{ x: number; y: number } | null>(null);
  // Prevent double deposit - track if deposit initiated for this popup instance
  const depositInitiatedRef = useRef(false);

  // Lock body scroll when open
  useEffect(() => {
    if (isOpen) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => { document.body.style.overflow = prev; };
    }
  }, [isOpen]);

  // Reset on open/close
  useEffect(() => {
    if (!isOpen) {
      vibrationController.stop();
      setIsScratching(false);
      setScratchedPercent(0);
      setRevealed(false);
      setAnimStep(0);
      setWalletAdded(false);
      depositInitiatedRef.current = false; // Reset deposit flag when popup closes
      lastPosRef.current = null;
    } else {
      const t1 = setTimeout(() => setAnimStep(1), 80);
      const t2 = setTimeout(() => setAnimStep(2), 400);
      return () => { clearTimeout(t1); clearTimeout(t2); };
    }
  }, [isOpen]);

  // Draw scratch layer — clean metallic gold, NO text
  useEffect(() => {
    if (!isOpen || revealed) return;
    const timer = setTimeout(() => {
      const canvas = canvasRef.current;
      if (!canvas) return;
      const ctx = canvas.getContext('2d');
      if (!ctx) return;

      ctx.clearRect(0, 0, canvas.width, canvas.height);

      // Rounded rect clip
      const r = 18;
      ctx.save();
      ctx.beginPath();
      ctx.moveTo(r, 0);
      ctx.lineTo(canvas.width - r, 0);
      ctx.quadraticCurveTo(canvas.width, 0, canvas.width, r);
      ctx.lineTo(canvas.width, canvas.height - r);
      ctx.quadraticCurveTo(canvas.width, canvas.height, canvas.width - r, canvas.height);
      ctx.lineTo(r, canvas.height);
      ctx.quadraticCurveTo(0, canvas.height, 0, canvas.height - r);
      ctx.lineTo(0, r);
      ctx.quadraticCurveTo(0, 0, r, 0);
      ctx.closePath();
      ctx.clip();

      // Base metallic gold gradient
      const base = ctx.createLinearGradient(0, 0, canvas.width, canvas.height);
      base.addColorStop(0,    '#b8860b');
      base.addColorStop(0.2,  '#ffd700');
      base.addColorStop(0.45, '#fff8dc');
      base.addColorStop(0.55, '#ffe066');
      base.addColorStop(0.75, '#d4a017');
      base.addColorStop(1,    '#996515');
      ctx.fillStyle = base;
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      // 3D bevel: light top-left
      const bevel = ctx.createLinearGradient(0, 0, 0, canvas.height);
      bevel.addColorStop(0, 'rgba(255,255,255,0.45)');
      bevel.addColorStop(0.15, 'rgba(255,255,255,0.12)');
      bevel.addColorStop(0.85, 'rgba(0,0,0,0.1)');
      bevel.addColorStop(1, 'rgba(0,0,0,0.3)');
      ctx.fillStyle = bevel;
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      // Diagonal sheen sweep
      const sheen = ctx.createLinearGradient(0, 0, canvas.width, canvas.height);
      sheen.addColorStop(0,    'rgba(255,255,255,0)');
      sheen.addColorStop(0.38, 'rgba(255,255,255,0)');
      sheen.addColorStop(0.48, 'rgba(255,255,255,0.5)');
      sheen.addColorStop(0.55, 'rgba(255,255,255,0.15)');
      sheen.addColorStop(1,    'rgba(255,255,255,0)');
      ctx.fillStyle = sheen;
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      // Fine crosshatch emboss texture for coin feel
      ctx.globalAlpha = 0.09;
      ctx.strokeStyle = '#fff';
      ctx.lineWidth = 0.6;
      for (let xi = 0; xi < canvas.width; xi += 6) {
        ctx.beginPath(); ctx.moveTo(xi, 0); ctx.lineTo(xi, canvas.height); ctx.stroke();
      }
      for (let yi = 0; yi < canvas.height; yi += 6) {
        ctx.beginPath(); ctx.moveTo(0, yi); ctx.lineTo(canvas.width, yi); ctx.stroke();
      }
      ctx.globalAlpha = 1;

      ctx.restore();
    }, 50);
    return () => clearTimeout(timer);
  }, [isOpen, revealed]);

  const getPos = (e: React.MouseEvent | React.TouchEvent, canvas: HTMLCanvasElement) => {
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    if ('touches' in e) {
      const touch = e.touches[0];
      return { x: (touch.clientX - rect.left) * scaleX, y: (touch.clientY - rect.top) * scaleY };
    }
    return { x: (e.clientX - rect.left) * scaleX, y: (e.clientY - rect.top) * scaleY };
  };

  const scratch = useCallback((x: number, y: number) => {
    const canvas = canvasRef.current;
    if (!canvas || revealed) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    ctx.globalCompositeOperation = 'destination-out';
    ctx.beginPath();
    ctx.arc(x, y, 30, 0, Math.PI * 2);
    ctx.fill();

    const soft = ctx.createRadialGradient(x, y, 18, x, y, 38);
    soft.addColorStop(0, 'rgba(0,0,0,1)');
    soft.addColorStop(1, 'rgba(0,0,0,0)');
    ctx.fillStyle = soft;
    ctx.beginPath();
    ctx.arc(x, y, 38, 0, Math.PI * 2);
    ctx.fill();

    if (lastPosRef.current) {
      ctx.beginPath();
      ctx.moveTo(lastPosRef.current.x, lastPosRef.current.y);
      ctx.lineTo(x, y);
      ctx.lineWidth = 54;
      ctx.lineCap = 'round';
      ctx.stroke();
    }
    lastPosRef.current = { x, y };
    ctx.globalCompositeOperation = 'source-over';

    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    let transparent = 0;
    for (let i = 3; i < imageData.data.length; i += 4) {
      if (imageData.data[i] === 0) transparent++;
    }
    const pct = Math.round((transparent / (canvas.width * canvas.height)) * 100);
    setScratchedPercent(pct);

    if (pct >= 50 && !revealed) {
      setRevealed(true);
      vibrationController.stop();
      vibrationController.win();
      ctx.clearRect(0, 0, canvas.width, canvas.height);
    }
  }, [revealed]);

  const handleMouseDown = (e: React.MouseEvent) => {
    if (revealed) return;
    isDrawingRef.current = true;
    setIsScratching(true);
    const canvas = canvasRef.current;
    if (!canvas) return;
    const pos = getPos(e, canvas);
    lastPosRef.current = pos;
    scratch(pos.x, pos.y);
  };
  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDrawingRef.current || revealed) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    scratch(getPos(e, canvas).x, getPos(e, canvas).y);
  };
  const handleMouseUp = () => {
    isDrawingRef.current = false;
    lastPosRef.current = null;
    vibrationController.stop();
  };
  const handleTouchStart = (e: React.TouchEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (revealed) return;
    isDrawingRef.current = true;
    setIsScratching(true);
    vibrationController.start();
    const canvas = canvasRef.current;
    if (!canvas) return;
    const pos = getPos(e, canvas);
    lastPosRef.current = pos;
    scratch(pos.x, pos.y);
  };
  const handleTouchMove = (e: React.TouchEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!isDrawingRef.current || revealed) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    scratch(getPos(e, canvas).x, getPos(e, canvas).y);
  };
  const handleTouchEnd = (e: React.TouchEvent) => {
    e.preventDefault();
    isDrawingRef.current = false;
    lastPosRef.current = null;
    vibrationController.stop();
  };

  useEffect(() => {
    if (revealed && !walletAdded && bonusAmount > 0 && !depositInitiatedRef.current) {
      depositInitiatedRef.current = true; // Mark deposit as initiated
      setWalletAdded(true);
      depositToWallet(bonusAmount).catch(console.error);
    }
  }, [revealed, walletAdded, bonusAmount]);

  const handleClaim = useCallback(() => {
    const newTotal = getWalletBalance();
    const txns = getWalletTransactions();
    onClose(bonusAmount, newTotal, txns);
  }, [bonusAmount, onClose]);

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-[80] flex items-center justify-center p-4"
      style={{ touchAction: 'none' }}
    >
      {/* Overlay */}
      <div className="absolute inset-0 bg-black/90 backdrop-blur-md" />

      {/* Floating coins */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        {[...Array(10)].map((_, i) => (
          <div
            key={i}
            className="absolute"
            style={{
              left: `${6 + i * 9}%`,
              bottom: '-8%',
              animation: `floatCoin3D ${2.4 + (i % 4) * 0.5}s linear infinite`,
              animationDelay: `${i * 0.25}s`,
            }}
          >
            <div
              className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-black text-white"
              style={{
                background: 'linear-gradient(135deg,#ffd700,#d4a017)',
                boxShadow: '0 4px 12px rgba(255,215,0,0.5), inset 0 1px 2px rgba(255,255,255,0.6)',
              }}
            >₹</div>
          </div>
        ))}
      </div>

      {/* 3D Card wrapper */}
      <div
        className="relative w-full max-w-sm"
        style={{
          transform: animStep >= 1 ? 'scale(1) translateY(0)' : 'scale(0.82) translateY(50px)',
          opacity: animStep >= 1 ? 1 : 0,
          transition: 'transform 0.55s cubic-bezier(.34,1.56,.64,1), opacity 0.4s ease',
          perspective: '1200px',
        }}
      >
        {/* Outer 3D shell */}
        <div
          style={{
            borderRadius: '28px',
            background: 'linear-gradient(160deg,#2d1b69,#1a0f3c,#0d0a1f)',
            boxShadow: `
              0 30px 60px rgba(0,0,0,0.7),
              0 0 0 1px rgba(255,215,0,0.25),
              0 0 40px rgba(255,215,0,0.1),
              inset 0 1px 0 rgba(255,255,255,0.12),
              inset 0 -2px 0 rgba(0,0,0,0.4)
            `,
            padding: '3px',
          }}
        >
          {/* Inner card */}
          <div
            style={{
              borderRadius: '26px',
              background: 'linear-gradient(160deg,#1e1047,#130a35)',
              overflow: 'hidden',
            }}
          >
            {/* Top shimmer bar */}
            <div style={{
              height: '3px',
              background: 'linear-gradient(90deg,transparent,#ffd700,#fff8,#ffd700,transparent)',
              backgroundSize: '200% 100%',
              animation: 'shimmerBar 2.5s ease-in-out infinite',
            }} />

            <div className="p-5">
              {/* Header */}
              <div
                className="text-center mb-4"
                style={{
                  opacity: animStep >= 2 ? 1 : 0,
                  transform: animStep >= 2 ? 'translateY(0)' : 'translateY(14px)',
                  transition: 'all 0.4s ease 0.1s',
                }}
              >
                <div className="flex items-center justify-center gap-2 mb-1">
                  <span className="text-xl">🪙</span>
                  <h2
                    className="font-black text-lg tracking-wide"
                    style={{
                      background: 'linear-gradient(135deg,#ffd700,#fff8a0,#d4a017)',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                      textShadow: 'none',
                    }}
                  >
                    SCRATCH &amp; WIN
                  </h2>
                  <span className="text-xl">🪙</span>
                </div>
                <p className="text-yellow-200/60 text-[11px] tracking-widest uppercase">
                  Order ₹{paidAmount} · {scratchPercent}% Bonus
                </p>
              </div>

              {/* ── 3D Scratch Card ── */}
              <div className="flex justify-center mb-4">
                <div
                  style={{
                    width: 270,
                    height: 190,
                    borderRadius: '20px',
                    position: 'relative',
                    // 3D depth effect
                    boxShadow: `
                      0 8px 0 #7a5200,
                      0 9px 0 #5a3a00,
                      0 12px 24px rgba(0,0,0,0.6),
                      inset 0 1px 0 rgba(255,255,255,0.3),
                      0 0 30px rgba(255,215,0,0.15)
                    `,
                    background: 'linear-gradient(135deg,#0d1b2a,#1a2f4a)',
                    opacity: animStep >= 2 ? 1 : 0,
                    transition: 'opacity 0.4s ease 0.3s',
                    transform: 'translateZ(0)',
                  }}
                >
                  {/* Reward layer behind canvas */}
                  <div className="absolute inset-0 flex flex-col items-center justify-center rounded-[20px] overflow-hidden">
                    {/* Radial bg glow */}
                    <div style={{
                      position: 'absolute', inset: 0,
                      background: 'radial-gradient(ellipse at center, rgba(255,215,0,0.12) 0%, transparent 70%)',
                    }} />

                    {revealed ? (
                      <>
                        <div
                          className="flex items-center gap-1 relative z-10"
                          style={{ animation: 'popIn3D 0.6s cubic-bezier(.34,1.56,.64,1)' }}
                        >
                          <span className="text-4xl">💰</span>
                          <span
                            className="font-black leading-none"
                            style={{
                              fontSize: '52px',
                              background: 'linear-gradient(135deg,#ffd700,#fff8a0,#d4a017,#ffd700)',
                              WebkitBackgroundClip: 'text',
                              WebkitTextFillColor: 'transparent',
                              filter: 'drop-shadow(0 0 12px rgba(255,215,0,0.8))',
                            }}
                          >
                            ₹{bonusAmount}
                          </span>
                          <span className="text-4xl">💰</span>
                        </div>
                        <div
                          className="flex items-center gap-1.5 mt-2 px-4 py-1 rounded-full relative z-10"
                          style={{
                            background: 'rgba(255,215,0,0.15)',
                            border: '1px solid rgba(255,215,0,0.4)',
                          }}
                        >
                          <i className="ri-wallet-3-fill text-yellow-400 text-xs" />
                          <span className="text-yellow-200 text-xs font-bold">Wallet Bonus Added!</span>
                        </div>
                      </>
                    ) : (
                      <div className="flex flex-col items-center gap-2 relative z-10">
                        <div
                          style={{
                            width: 72, height: 72, borderRadius: '50%',
                            background: 'linear-gradient(135deg,rgba(255,215,0,0.12),rgba(255,215,0,0.04))',
                            border: '2px dashed rgba(255,215,0,0.35)',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            boxShadow: '0 0 20px rgba(255,215,0,0.15)',
                          }}
                        >
                          <span
                            className="font-black"
                            style={{
                              fontSize: '44px', lineHeight: 1,
                              background: 'linear-gradient(135deg,#ffd700,#b8860b)',
                              WebkitBackgroundClip: 'text',
                              WebkitTextFillColor: 'transparent',
                            }}
                          >?</span>
                        </div>
                        <p className="text-yellow-400/80 text-xs font-semibold tracking-wide">SCRATCH TO REVEAL</p>
                      </div>
                    )}

                    {/* Win sparkles */}
                    {revealed && [...Array(14)].map((_, i) => (
                      <div
                        key={i}
                        className="absolute text-yellow-300 pointer-events-none"
                        style={{
                          fontSize: `${14 + (i % 3) * 7}px`,
                          left: `${10 + (i * 6) % 80}%`,
                          top: `${8 + (i * 9) % 84}%`,
                          animation: 'sparkle3D 0.9s ease-in-out infinite',
                          animationDelay: `${i * 0.08}s`,
                        }}
                      >✦</div>
                    ))}
                  </div>

                  {/* Canvas overlay */}
                  {!revealed && (
                    <canvas
                      ref={canvasRef}
                      width={270}
                      height={190}
                      className="absolute inset-0 w-full h-full"
                      style={{
                        borderRadius: '20px',
                        cursor: isScratching ? 'none' : 'crosshair',
                        touchAction: 'none',
                      }}
                      onMouseDown={handleMouseDown}
                      onMouseMove={handleMouseMove}
                      onMouseUp={handleMouseUp}
                      onMouseLeave={handleMouseUp}
                      onTouchStart={handleTouchStart}
                      onTouchMove={handleTouchMove}
                      onTouchEnd={handleTouchEnd}
                    />
                  )}

                  {/* Progress bar */}
                  {!revealed && (
                    <div
                      className="absolute bottom-0 left-0 right-0 pointer-events-none"
                      style={{ height: '3px', background: 'rgba(0,0,0,0.4)', borderRadius: '0 0 20px 20px' }}
                    >
                      <div
                        style={{
                          height: '100%',
                          width: `${scratchedPercent}%`,
                          background: 'linear-gradient(90deg,#b8860b,#ffd700)',
                          borderRadius: '0 0 0 20px',
                          transition: 'width 0.2s ease',
                          boxShadow: '0 0 8px rgba(255,215,0,0.5)',
                        }}
                      />
                    </div>
                  )}

                  {/* 3D bottom edge depth line */}
                  <div style={{
                    position: 'absolute', bottom: '-8px', left: '4px', right: '4px',
                    height: '8px', borderRadius: '0 0 16px 16px',
                    background: 'linear-gradient(180deg,#7a5200,#3d2800)',
                  }} />
                </div>
              </div>

              {/* Hint / progress */}
              {!revealed && (
                <p className="text-center text-yellow-200/50 text-[11px] mb-4 tracking-wide">
                  {scratchedPercent < 8
                    ? '👆 Yahan scratch karo...'
                    : scratchedPercent < 40
                      ? `${scratchedPercent}% done — thoda aur!`
                      : '🔥 Almost there!'}
                </p>
              )}

              {/* CTA after reveal */}
              {revealed && (
                <div
                  className="text-center"
                  style={{ animation: 'popIn3D 0.5s cubic-bezier(.34,1.56,.64,1)' }}
                >
                  <div className="flex items-center justify-center gap-2 mb-3">
                    <div
                      className="flex items-center gap-2 rounded-xl px-4 py-2"
                      style={{
                        background: 'rgba(16,185,129,0.12)',
                        border: '1px solid rgba(16,185,129,0.35)',
                        boxShadow: '0 0 16px rgba(16,185,129,0.2)',
                      }}
                    >
                      <i className="ri-checkbox-circle-fill text-green-400 text-lg" />
                      <span className="text-green-300 font-bold text-sm">₹{bonusAmount} Wallet mein add!</span>
                    </div>
                  </div>

                  <button
                    onClick={handleClaim}
                    className="w-full py-4 rounded-2xl font-black text-base cursor-pointer whitespace-nowrap transition-all hover:scale-105 active:scale-95"
                    style={{
                      background: 'linear-gradient(135deg,#b8860b,#ffd700,#b8860b)',
                      backgroundSize: '200% 100%',
                      animation: 'shimmerBtn 2s ease-in-out infinite',
                      color: '#1a0f00',
                      boxShadow: '0 6px 0 #7a5200, 0 8px 20px rgba(255,215,0,0.4)',
                      textShadow: '0 1px 0 rgba(255,255,255,0.4)',
                    }}
                  >
                    <i className="ri-wallet-3-fill mr-2" />
                    Claim ₹{bonusAmount} Bonus!
                  </button>
                  <p className="text-yellow-200/40 text-[11px] mt-2.5">Wallet mein auto-add ho gaya hai</p>
                </div>
              )}

              {/* Footer */}
              <div
                className="mt-4 rounded-xl px-4 py-2.5 flex items-center justify-center gap-2"
                style={{
                  background: 'rgba(255,215,0,0.04)',
                  border: '1px solid rgba(255,215,0,0.12)',
                }}
              >
                <i className="ri-checkbox-circle-line text-green-400 text-sm" />
                <p className="text-yellow-200/50 text-[11px]">
                  Order confirmed · <span className="text-yellow-400 font-semibold">₹{paidAmount}</span> Cash on Delivery
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes floatCoin3D {
          0%   { transform: translateY(0) rotateY(0deg); opacity: 1; }
          80%  { opacity: 0.8; }
          100% { transform: translateY(-115vh) rotateY(720deg); opacity: 0; }
        }
        @keyframes sparkle3D {
          0%, 100% { opacity: 0.2; transform: scale(0.7) rotate(0deg); }
          50%       { opacity: 1; transform: scale(1.5) rotate(20deg); }
        }
        @keyframes popIn3D {
          0%   { transform: scale(0.5) translateY(20px); opacity: 0; }
          60%  { transform: scale(1.08) translateY(-4px); opacity: 1; }
          100% { transform: scale(1) translateY(0); opacity: 1; }
        }
        @keyframes shimmerBar {
          0%   { background-position: -200% 0; }
          100% { background-position: 200% 0; }
        }
        @keyframes shimmerBtn {
          0%   { background-position: 0% 50%; }
          50%  { background-position: 100% 50%; }
          100% { background-position: 0% 50%; }
        }
      `}</style>
    </div>
  );
}
